import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { StoreContext } from 'store';
import { formatImageUrl, isProduction, useWindowDimensions } from 'helpers';
import { Icon } from '../Icons';

import './menu.scss';
import { useMarketBannersContext } from '../ContextBanners';
import { Locale } from '../../localisation';
import { Text } from 'components/Text';
import { MARKET_URL } from 'consts/market';

type Url = {
  url: string | { [key in Locale]: string };
  external: boolean;
  local: boolean;
  icon: string;
  withNotificationIcon?: boolean;
  hide?: boolean;
  selfTarget?: boolean;
};

const getUrls = (hideMarket: boolean, isOtherPage: boolean): Record<string, Url> => ({
  searchPlayer: {
    url: isOtherPage ? 'https://club.tacticool.game/' : '/',
    external: isOtherPage,
    selfTarget: true,
    local: false,
    icon: formatImageUrl('/img/icons/search.svg'),
  },
  downloadGame: {
    url: '#download-links',
    external: false,
    local: true,
    icon: formatImageUrl('/img/icons/download.svg'),
  },
  about: {
    url: 'https://tacticool.game/',
    external: true,
    local: false,
    icon: formatImageUrl('/img/icons/about.svg'),
  },
  market: {
    url: {
      ru: MARKET_URL['ru'],
      en: MARKET_URL['en'],
    },
    hide: hideMarket,
    external: true,
    local: false,
    icon: formatImageUrl('/img/icons/market.svg'),
    withNotificationIcon: true,
  },
});

function isLocaleUrl(obj: any): obj is Record<Locale, string> {
  return typeof obj === 'object' && obj !== null;
}

const showMarketBlock = (event: React.MouseEvent) => {
  event.preventDefault();

  if (window.innerWidth >= 900) {
    window.open('https://tacticool.onelink.me/jIkU/PC');
  } else {
    document?.querySelector('#download-links')?.scrollIntoView();
  }
};

interface MenuItemsProps {
  isOpened: boolean;
}

const MenuItems: React.FC<MenuItemsProps> = ({ isOpened }) => {
  const { width } = useWindowDimensions();
  const {
    translation: t,
    localisation: [locale],
  } = useContext(StoreContext);
  const { hideMarketHeaderLink, hideAll } = useMarketBannersContext();
  const hideMarketLinkVisible = hideMarketHeaderLink || hideAll;
  const subdomain = window.location.host?.split('.')[0];

  const items = Object.entries(
    getUrls(hideMarketLinkVisible, isProduction() && subdomain !== 'club'),
  ).map(
    ([
      key,
      { url, external, local, selfTarget, icon, withNotificationIcon, hide },
    ]) => {
      const uri = isLocaleUrl(url) ? url[locale || 'en'] : url;

      const RenderItemBody = () => (
        <div className="container">
          <img src={icon} alt={t.mainMenu[key]} />
          <Text.CapsM>{t.mainMenu[key]}</Text.CapsM>
          {withNotificationIcon && (
            <Icon.MarketNotification className="market_notification_icon" />
          )}
        </div>
      );

      if (hide) {
        return null;
      }

      const renderLink = (ItemBody: React.ElementType) => {
        if (local) {
          return (
            <a onClick={showMarketBlock} href={uri} rel="noreferrer">
              <ItemBody />
            </a>
          );
        }

        if (external) {
          return (
            <a target={selfTarget ? '_self' : '_blank'} href={uri} rel="noreferrer">
              <ItemBody />
            </a>
          );
        }

        return (
          <Link to={uri}>
            <ItemBody />
          </Link>
        );
      };

      return <li key={key}>{renderLink(RenderItemBody)}</li>;
    },
  );

  return width >= 900 ? (
    <div className={'c-menu-items ' + (isOpened ? 'opened' : '')}>{items}</div>
  ) : (
    createPortal(
      <div className={'c-menu-items ' + (isOpened ? 'opened' : '')}>{items}</div>,
      document?.querySelector('body')!,
    )
  );
};

interface MenuProps {
  isOpened: boolean;
  onClick: () => void;
}

const Menu: React.FC<MenuProps> = ({ isOpened, onClick }) => {
  return (
    <div className="c-menu">
      <div
        className={'c-menu__button ' + (isOpened ? 'opened' : '')}
        onClick={onClick}
      >
        <span />
        <span />
        <span />
      </div>
    </div>
  );
};

export { Menu, MenuItems };
